// @givmo/board — the agenda match, and what the officer decided about it.
//
// ══ THE SUGGEST → CONFIRM-OR-DISMISS SEAM, WHICH IS THE WHOLE CONTROL ══════════════════════════
//
// Plan §3.2 admits a machine suggestion ONLY inside a confirm-or-correct control. This panel is that
// control, and the seam is exact: a candidate has no row anywhere, and never will. What gets stored is
// the OFFICER'S DECISION — a confirmation or a dismissal — plus `recorded_via`, the provenance saying
// whether a machine put the candidate in front of them. The machine's own words are never persisted.
//
//   * A CANDIDATE IS NEVER SHOWN AS AN ASSERTED CONFLICT. It is shown as a text match, with the name
//     that was looked for, and with the method that found it. Nothing here says a conflict exists.
//   * DISMISSING IS A DECISION, NOT A DISMISSAL FROM THE SCREEN. It writes a row. "A human considered
//     this and said no" is worth more than a missing row, which is indistinguishable from nobody
//     having looked — and it is what keeps the same candidate from being put up again every time.
//   * NO CLIENT-SIDE MATCHING, SCORING, RANKING OR RE-FILTERING. See `conflicts-decode.ts`.
//
// ══ THE METHOD STATEMENT IS LOAD-BEARING, NOT DECORATION ═══════════════════════════════════════
//
// The dangerous reading of an empty candidate list is "the module checked and found nothing to
// disclose". The only thing that forecloses it is an honest statement of what was and was not looked
// at, travelling with the answer — which is why the backend attaches `method` to EVERY suggestion
// response rather than documenting it somewhere.
//
// So this panel renders that statement verbatim, above the candidates, whether or not there are any —
// and **refuses to render candidates or an emptiness at all when the statement is missing.** A
// response with no method is shown as a read we could not show. That is not defensive coding: a
// candidate list without its method is exactly the artifact §3.2 forbids, and an EMPTY one without its
// method is worse, because it reads as a clean bill.
//
// ══ WHY THERE IS A MANUAL LINK PATH AS WELL AS THE SUGGESTIONS ═════════════════════════════════
//
// The matcher reads ONE field — the counterparty name — and a self-interest disclosure has no
// counterparty: a director disclosing their own compensation names no third party, so there is nothing
// for the match to look for and no candidate will ever appear. That is the ordinary shape of the most
// consequential disclosure a board handles, and it is the shape of the §53.4958-6(c)(1)(ii) case the
// backend's own corpus encodes. A surface with only the suggestion path could not record it at all.
// So the officer can name a disclosure and an item themselves, and that link is stored with
// `recorded_via: officer_entry` — the honest provenance that no machine was involved.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractList } from '@givmo/api-client/list';
import {
  isGivmoError,
  type BoardAgendaItemOut,
  type BoardConflictLinkWrite,
  type BoardMotionOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Select,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { RegisteredDisclaimer } from '../../components/registered-disclaimer';
import { displayDate } from '../../lib/display';
import { displayInstantInZone } from '../meetings/zoned-time';
import {
  decodeSuggestionList,
  extractDisclosures,
  extractLinks,
  linkStatusLabel,
  linkStatusTone,
  linkTargetLabel,
  recordedViaLabel,
  type ConflictLinkRow,
  type ConflictSuggestionRow,
} from '../conflicts/conflicts-decode';
import { useBoardOperations } from '../../board-runtime';
export interface ConflictsPanelProps {
  orgRef: string;
  meetingId: string;
  /** The MEETING's zone. Required — an officer's act recorded in the room belongs to its calendar. */
  timeZone: string | null;
  readOnly: boolean;
}

export function ConflictsPanel({ orgRef, meetingId, timeZone, readOnly }: ConflictsPanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);
  const enabled = orgRef.length > 0 && meetingId.length > 0;

  const suggestionsOptions = operations.officerListBoardConflictSuggestionsOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const suggestionsQuery = useQuery({
    ...suggestionsOptions,
    enabled,
    retry: false,
    select: decodeSuggestionList,
  });

  const linksOptions = operations.officerListBoardConflictLinksOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const linksQuery = useQuery({
    ...linksOptions,
    enabled,
    retry: false,
    select: extractLinks,
  });

  const links = linksQuery.data ?? [];
  const suggestions = suggestionsQuery.data ?? null;

  /**
   * A decision invalidates BOTH reads, and both are needed.
   *
   * The links list obviously changes. The suggestion read changes too, and less obviously: it
   * subtracts pairs a human has already decided, so a candidate just confirmed or dismissed must
   * disappear from it — leaving it stale would put the same candidate back in front of the officer
   * with a decision already recorded against it.
   */
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: suggestionsOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: linksOptions.queryKey }),
    ]);
  };

  const decide = useMutation({
    mutationFn: async (input: { body: BoardConflictLinkWrite }) => {
      await operations.officerRecordBoardConflictLink({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: input.body,
        throwOnError: true,
      });
    },
    onSuccess: () => void invalidate(),
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  const removeLink = useMutation({
    mutationFn: async (link: ConflictLinkRow) => {
      await operations.officerDeleteBoardConflictLink({
        path: { org_ref: orgRef, meeting_id: meetingId, link_id: link.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Removed.');
      void invalidate();
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      );
    },
  });

  /** Record a decision about a candidate the match put in front of the officer. */
  function decideCandidate(candidate: ConflictSuggestionRow, status: 'confirmed' | 'dismissed') {
    decide.mutate({
      body: {
        disclosure_record_id: candidate.disclosure_record_id,
        link_target: candidate.link_target === 'motion' ? 'motion' : 'agenda_item',
        agenda_item_id: candidate.agenda_item_id,
        motion_id: candidate.motion_id,
        status,
        // The provenance half of the control: a machine put this in front of them. It says nothing
        // about the link itself, and it is all that survives of the suggestion.
        recorded_via: 'confirmed_suggestion',
      },
    });
  }

  return (
    <Card testId="conflicts-panel">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Disclosures and this meeting
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Which recorded disclosures name something on this agenda — as you decide it. A link
              you record here appears beside the motion before the board votes, and enters the
              minutes when the draft is next refreshed.
            </p>
          </div>
          {!readOnly ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setLinking(true)}
              testId="conflicts-link-manually"
            >
              Link one yourself
            </Button>
          ) : null}
        </div>

        <RegisteredDisclaimer
          surface="BOARD_SELF_REPORTED"
          orgRef={orgRef}
          testId="conflicts-registered-disclaimer"
        />

        <SuggestionsSection
          state={
            suggestionsQuery.isPending ? 'loading' : suggestionsQuery.isError ? 'error' : 'ready'
          }
          list={suggestions}
          readOnly={readOnly}
          busy={decide.isPending}
          onDecide={decideCandidate}
        />

        <RecordedDecisions
          links={links}
          loading={linksQuery.isPending}
          failed={linksQuery.isError}
          timeZone={timeZone}
          readOnly={readOnly}
          onRemove={(link) => removeLink.mutate(link)}
        />
      </CardBody>

      {linking ? (
        <ManualLinkDialog
          orgRef={orgRef}
          meetingId={meetingId}
          onClose={() => setLinking(false)}
          onRecorded={() => {
            setLinking(false);
            void invalidate();
          }}
        />
      ) : null}
    </Card>
  );
}

// ── The candidates ───────────────────────────────────────────────────────────────────────────────

function SuggestionsSection({
  state,
  list,
  readOnly,
  busy,
  onDecide,
}: {
  state: 'loading' | 'error' | 'ready';
  list: { method: string | null; suggestions: ConflictSuggestionRow[] } | null;
  readOnly: boolean;
  busy: boolean;
  onDecide: (candidate: ConflictSuggestionRow, status: 'confirmed' | 'dismissed') => void;
}) {
  if (state === 'loading') {
    return (
      <p
        className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
        data-testid="conflicts-suggestions-loading"
      >
        Looking…
      </p>
    );
  }

  // ⚠ AN ERROR, AND A METHOD-LESS RESPONSE, LAND IN THE SAME PLACE — and neither may render an empty
  // list. Saying "no candidates" without the statement of what was looked at is the one reading this
  // whole control exists to foreclose, and it is worse when it is wrong: an officer told there is
  // nothing has been told something the module cannot know.
  const method = list?.method ?? null;
  if (state === 'error' || list === null || method === null) {
    return (
      <div
        className="flex flex-col gap-4 rounded-md border border-border-subtle bg-surface-sunken p-12"
        data-testid="conflicts-suggestions-unavailable"
      >
        <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          We couldn’t show the text match just now
        </span>
        <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Nothing is shown rather than a partial answer, because an empty list here would read as
          though something had been looked at. Reload the page to try again — and you can still link
          a disclosure yourself.
        </p>
      </div>
    );
  }

  const candidates = list.suggestions;

  return (
    <div className="flex flex-col gap-10" data-testid="conflicts-suggestions">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        Names from your register that appear in this agenda
      </span>

      {/* ⚠ THE BACKEND'S METHOD STATEMENT, VERBATIM AND ALWAYS ABOVE THE RESULT — including when the
          result is empty, which is the case it matters most in. Never paraphrased, never truncated,
          never behind a disclosure triangle. */}
      <p
        className="rounded-md border border-border-subtle bg-surface-sunken p-12 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary"
        data-testid="conflicts-suggestions-method"
      >
        {method}
      </p>

      {candidates.length === 0 ? (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="conflicts-suggestions-empty"
        >
          {/* Phrased as what the match DID, never as a finding about the meeting. The statement above
              carries the rest. */}
          This text match found none of your recorded counterparty names in the wording of this
          meeting’s agenda items and motions.
        </p>
      ) : (
        <ul className="flex flex-col gap-10" data-testid="conflicts-suggestions-list">
          {candidates.map((candidate) => (
            <li
              key={`${candidate.disclosure_record_id}:${candidate.link_target}:${candidate.motion_id ?? candidate.agenda_item_id ?? ''}`}
              className="flex flex-col gap-8 rounded-lg border border-border-subtle p-12"
              data-testid={`conflict-candidate-${candidate.disclosure_record_id}`}
            >
              {/* The backend's sentence about this candidate, verbatim. */}
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong">
                {candidate.statement}
              </p>

              <div className="flex flex-wrap items-center gap-6">
                <Badge tone="neutral">{linkTargetLabel(candidate.link_target)}</Badge>
                {candidate.disclosing_member_name !== null ? (
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
                    {candidate.disclosing_member_name}
                  </span>
                ) : null}
                {candidate.disclosed_on !== null ? (
                  <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                    · disclosed {displayDate(candidate.disclosed_on)}
                  </span>
                ) : null}
              </div>

              <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                {/* WHY it is in front of them — so the officer can see the basis rather than be asked
                    to trust the match. */}
                The name looked for: {candidate.matched_name}
              </span>

              {!readOnly ? (
                <div className="flex flex-wrap justify-end gap-8">
                  {/* Both are recorded acts, and the UI gives them equal weight on purpose — a
                      dismissal is a decision, not a way of closing the row. */}
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => onDecide(candidate, 'dismissed')}
                    testId={`conflict-candidate-dismiss-${candidate.disclosure_record_id}`}
                  >
                    It does not apply
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => onDecide(candidate, 'confirmed')}
                    testId={`conflict-candidate-confirm-${candidate.disclosure_record_id}`}
                  >
                    It names this
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── What the officer decided ─────────────────────────────────────────────────────────────────────

function RecordedDecisions({
  links,
  loading,
  failed,
  timeZone,
  readOnly,
  onRemove,
}: {
  links: readonly ConflictLinkRow[];
  loading: boolean;
  failed: boolean;
  timeZone: string | null;
  readOnly: boolean;
  onRemove: (link: ConflictLinkRow) => void;
}) {
  return (
    <div className="flex flex-col gap-10" data-testid="conflicts-decisions">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        What you have decided about this meeting
      </span>

      {failed ? (
        <p
          className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
          data-testid="conflicts-decisions-error"
        >
          We couldn’t load your recorded decisions just now.
        </p>
      ) : links.length === 0 ? (
        <p
          className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
          data-testid="conflicts-decisions-empty"
        >
          {loading
            ? 'Loading…'
            : readOnly
              ? 'No decision has been recorded about this meeting yet.'
              : 'You have not recorded a decision about this meeting yet.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-10" data-testid="conflicts-decisions-list">
          {links.map((link) => (
            <li
              key={link.id}
              className="flex flex-col gap-6 rounded-lg border border-border-subtle p-12"
              data-testid={`conflict-link-${link.id}`}
            >
              <div className="flex flex-wrap items-center gap-6">
                <Badge tone={linkStatusTone(link.status)}>{linkStatusLabel(link.status)}</Badge>
                <Badge tone="neutral">{linkTargetLabel(link.link_target)}</Badge>
              </div>

              {/* Verbatim. On this list the backend's sentence deliberately does NOT mention a recusal
                  — there is no motion in question here — and that difference is why it is rendered
                  rather than composed. */}
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-strong"
                data-testid={`conflict-link-statement-${link.id}`}
              >
                {link.statement}
              </p>

              {link.note !== null ? (
                <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  Your note: {link.note}
                </span>
              ) : null}

              <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                {recordedViaLabel(link.recorded_via)} ·{' '}
                {displayInstantInZone(link.recorded_at, timeZone)}
              </span>

              {!readOnly ? (
                <div className="flex justify-end">
                  {/* There is no EDIT, on either side of the wire: a decision is recorded or it is
                      not, so changing one is removing it and recording the other. */}
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    onClick={() => onRemove(link)}
                    testId={`conflict-link-remove-${link.id}`}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Linking one by hand ──────────────────────────────────────────────────────────────────────────

function ManualLinkDialog({
  orgRef,
  meetingId,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  meetingId: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [disclosureId, setDisclosureId] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The WHOLE register, not just what stands today. A meeting may be minuting something that happened
  // while an earlier disclosure was still in force, and the backend allows a superseded disclosure to
  // be linked for exactly that reason — only the SUGGESTIONS filter on what stands.
  const registerQuery = useQuery({
    ...operations.officerListBoardConflictRegisterOptions({
      path: { org_ref: orgRef },
    }),
    retry: false,
    select: extractDisclosures,
  });

  const agendaQuery = useQuery({
    ...operations.officerListBoardAgendaItemsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: (body: unknown) => extractList<BoardAgendaItemOut>(body, 'data'),
  });

  const motionsQuery = useQuery({
    ...operations.officerListBoardMotionsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: (body: unknown) => extractList<BoardMotionOut>(body, 'data'),
  });

  const disclosures = registerQuery.data ?? [];
  const agendaItems = agendaQuery.data ?? [];
  const motions = motionsQuery.data ?? [];

  // One picker over both kinds, prefixed so the chosen value carries WHICH kind it is. A link names
  // exactly one thing and the backend refuses both or neither, so the two ids can never both be set.
  const targetOptions = [
    ...agendaItems.map((item) => ({
      value: `agenda_item:${item.id}`,
      label: `Agenda — ${item.title}`,
    })),
    ...motions.map((motion) => ({
      value: `motion:${motion.id}`,
      label: `Motion — ${truncate(motion.motion_text)}`,
    })),
  ];

  const save = useMutation({
    mutationFn: async () => {
      if (disclosureId === null || target === null) return;
      const [kind, id] = splitTarget(target);
      await operations.officerRecordBoardConflictLink({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: {
          disclosure_record_id: disclosureId,
          link_target: kind,
          agenda_item_id: kind === 'agenda_item' ? id : null,
          motion_id: kind === 'motion' ? id : null,
          // A confirmation: naming a disclosure and an item by hand IS recording that it names it.
          // Dismissal has no meaning without a candidate to dismiss, so it is not offered here.
          status: 'confirmed',
          // No machine was involved. Recording anything else would misstate the provenance of the act.
          recorded_via: 'officer_entry',
          note: note.trim() || null,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Link recorded.');
      onRecorded();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t save that. Please try again.');
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label="Link a disclosure to this meeting"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="conflict-link-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Link a disclosure to this meeting
        </Dialog.Title>

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* Says why this path exists, in the words of the case that needs it. */}
          Use this where the text match cannot help — a director disclosing their own compensation
          names no other party, so there is no name for it to look for.
        </p>

        <Select
          label="Which disclosure"
          appearance="saas"
          placeholder="Choose a disclosure"
          value={disclosureId}
          onValueChange={(next) => setDisclosureId(next)}
          options={disclosures.map((disclosure) => ({
            value: disclosure.record_id,
            label: disclosureOptionLabel(disclosure.disclosing_member_name, disclosure.title),
          }))}
          testId="conflict-link-disclosure"
        />
        <FieldNote testId="conflict-link-disclosure-note">
          A disclosure a later one has replaced is still listed — a meeting may be minuting
          something that happened while it was in force.
        </FieldNote>

        <Select
          label="Which agenda item or motion"
          appearance="saas"
          placeholder="Choose one"
          value={target}
          onValueChange={(next) => setTarget(next)}
          options={targetOptions}
          testId="conflict-link-target"
        />
        <FieldNote>
          A link names exactly one thing. A link you record against an agenda item stays recorded
          that way, even where it appears beside a motion made under it.
        </FieldNote>

        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          testId="conflict-link-note"
        />

        {error !== null ? <ErrorMessage testId="conflict-link-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="conflict-link-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={disclosureId === null || target === null || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="conflict-link-save"
          >
            Record the link
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** `agenda_item:<id>` / `motion:<id>` → the two fields the wire wants. */
function splitTarget(value: string): ['agenda_item' | 'motion', string] {
  const separator = value.indexOf(':');
  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  return [kind === 'motion' ? 'motion' : 'agenda_item', id];
}

/** A motion's wording, shortened for a picker. Display only — the id is what is sent. */
function truncate(value: string): string {
  const trimmed = value.trim();
  return trimmed.length <= 70 ? trimmed : `${trimmed.slice(0, 70)}…`;
}

function disclosureOptionLabel(memberName: string | null, title: string): string {
  return memberName === null ? title : `${memberName} — ${title}`;
}
